import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import WarningAlert from 'Components/UI/WarningAlert';
import BreadCrumb, { IBreadcrumbItem } from 'Components/UI/BreadCrumb';
import PageHeader from 'Components/UI/PageHeader/PageHeader';
import BoxPlan from '../Components/BoxPlan';
import { RevenuesActions } from 'Store/Revenues/Revenues.actions';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import ExitRedirect from '../Modals/ExitRedirect';
import Button from 'Components/UI/Button';
import BoxDownResources from '../Components/BoxDownResources';
import Table from 'Components/UI/Table';
import { getColumnsConstructionSitesInactivate, getColumnsTenantsInactivate } from './Downgrade.columns';
import { Skeleton } from 'antd';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import { ITenantUsersResponse } from 'Data/interfaces/Tenant/ITenantUsersResponse';
import useHistoryPush from 'Hooks/useHistoryPush';
import { getCurrentTenant, getTenants } from 'Store/Tenant/Tenant.selector';
import i18n from 'i18n';
import styles from './Downgrade.module.scss';

const getBreadCrumb = (): IBreadcrumbItem[] => [
  {
    description: i18n.t('faturamento:breadcrumbs.faturamento'),
    href: '/faturamento',
  },
  {
    description: i18n.t('faturamento:breadcrumbs.alterarPlano'),
    href: '/faturamento/planos',
  },
  {
    description: i18n.t('faturamento:breadcrumbs.dadosFaturamento'),
    href: '/faturamento/planos/dadosfaturamento',
  },
  {
    description: i18n.t('faturamento:breadcrumbs.downgrade'),
    href: '/faturamento/planos/dadosfaturamento/downgrade',
  },
];

const Downgrade: FC<Props> = ({
  showModalExitDowngrade,
  downgradeRequest,
  changePlan,
  userInfo,
  getDowngradeInfo,
  isLoading,
  downgradeRequestLoading
}) => {
  const { t } = useTranslation(['faturamento', 'common']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenants = useSelector(getTenants);
  const currentTenant = useSelector(getCurrentTenant);
  const { historyPush } = useHistoryPush();

  const [disabled, setDisabled] = useState(true);
  const [selectedRowCS, setSelectedRowCS] = useState([] as IObraResponse[]);
  const [selectedRowTenant, setSelectedRowTenant] = useState([] as ITenantUsersResponse[]);
  const [maxLimitsCS, setMaxLimitsCS] = useState(0);
  const [maxLimitsTenants, setMaxLimitsTenants] = useState(0);
  const [requestSend, setRequestSend] = useState(false);

  useEffect(() => {
    dispatch(RevenuesActions.cancelPageDowgrade(true));
  }, []);

  useEffect(() => {
    if (requestSend && !downgradeRequestLoading) {
      dispatch(RevenuesActions.cancelPageDowgrade(false));
      navigate('/faturamento');
    }
  }, [requestSend, downgradeRequestLoading]);

  useEffect(() => {
    if (downgradeRequest.showWarning === 1 &&
      (selectedRowCS.length >= maxLimitsCS) &&
      (selectedRowTenant.length >= maxLimitsTenants) &&
      ((maxLimitsCS && maxLimitsTenants) !== 0)
    ) {
      return setDisabled(false);
    }
    if (downgradeRequest.showWarning === 2 &&
      (selectedRowCS.length >= maxLimitsCS) &&
      (maxLimitsCS !== 0)
    ) {
      return setDisabled(false);
    }
    if (downgradeRequest.showWarning === 3 &&
      (selectedRowTenant.length >= maxLimitsTenants) &&
      (maxLimitsTenants !== 0)
    ) {
      return setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [downgradeRequest.showWarning, maxLimitsCS, maxLimitsTenants, selectedRowCS, selectedRowTenant]);

  useEffect(() => {
    if (userInfo?.QtyActiveConstructionSites && changePlan?.MaxQtyConstructionSites) {
      setMaxLimitsCS(userInfo?.QtyActiveConstructionSites - changePlan?.MaxQtyConstructionSites);
    }
    if (userInfo?.QtyActiveUsers && changePlan?.MaxQtyUsers) {
      setMaxLimitsTenants(userInfo?.QtyActiveUsers - changePlan?.MaxQtyUsers);
    }
  }, [changePlan, userInfo]);

  useEffect(() => {
    if (userInfo?.UserStoreItem.StorePlan?.StorePlansId) {
      dispatch(RevenuesActions.getDowgradeInfo(userInfo?.UserStoreItem.StorePlan?.StorePlansId));
    }
  }, [userInfo, changePlan]);

  const onClose = () => {
    dispatch(RevenuesActions.showModalExitDowgrade(false));
  };

  const handlePlanChange = () => {
    if (changePlan && downgradeRequest.billingData) {
      dispatch(RevenuesActions.upgradeOrDowngradePlan({
        updateStorePlanId: changePlan?.StorePlansId,
        billingData: downgradeRequest.billingData,
        constructionSiteIdsToDisable: selectedRowCS.map((cs) => cs.ConstructionSiteId) ?? [],
        tenantUserIdsToDisable: selectedRowTenant.filter((tenant) => tenant.TenantInviteId === 0).map((tenant) => tenant.TenantUserId) ?? [],
        tenantUserInviteIdsToRemove: selectedRowTenant.filter((tenant) => tenant.TenantUserId === 0).map((tenant) => tenant.TenantInviteId) ?? [],
        userInfoTracking: userInfo,
        currentTenantTracking: currentTenant,
        tenantIds: tenants && tenants.length > 0
          ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
          : undefined,
      }));
      setRequestSend(true);
    }
  }

  const buttonPlanDowngrade = (
    <div>
      <Button
        type={disabled ? "dashed" : "primary"}
        disabled={disabled}
        width={133}
        className={disabled ? styles['confirm-button-box-plan'] : ''}
        onClick={handlePlanChange}
        loading={downgradeRequestLoading}
      >
        {t('common:confirm')}
      </Button>
      <Button
        type="text"
        width={123}
        className={styles['cancel-button-box-plan']}
        onClick={() => historyPush('/faturamento')}
      >
        {t('common:cancel')}
      </Button>
    </div>
  );
  const onChangeCS = (selectedRowKeys: any, selectedRows: any) => setSelectedRowCS(selectedRows);

  const onChangeTenant = (selectedRowKeys: any, selectedRows: any) => setSelectedRowTenant(selectedRows);

  const rowSelectionCS = {
    selectedRowCS,
    onChange: onChangeCS,
  };

  const rowSelectionTenant = {
    selectedRowTenant,
    onChange: onChangeTenant,
  };


  return (
    <>
      <PageHeader name="Complete o downgrade da sua conta" />
      <BreadCrumb breadcrumbs={getBreadCrumb()} />

      <div className={styles['Grid']}>
        <div className={styles['grid-left']}>
          {downgradeRequest.showWarning === 1 && <WarningAlert
            message={t('faturamento:downgrade.archiveAndInactivate')}
            description={t('faturamento:downgrade.archiveAndInactivateDesc')}
            type="warning"
            showIcon
            closable
          />}
          {downgradeRequest.showWarning === 2 && <WarningAlert
            message={t('faturamento:downgrade.archiveWorks')}
            description={t('faturamento:downgrade.archiveWorksDesc')}
            type="warning"
            showIcon
            closable
          />}
          {downgradeRequest.showWarning === 3 && <WarningAlert
            message={t('faturamento:downgrade.inactivateUsers')}
            description={t('faturamento:downgrade.inactivateUsersDesc')}
            type="warning"
            showIcon
            closable
          />}

          {(downgradeRequest.showWarning === 1 ||
            downgradeRequest.showWarning === 2) &&
            <>
              {isLoading ? (
                <Skeleton active />
              ) : (
                <BoxDownResources
                  title={t('faturamento:downgrade.archiveWorksTitle')}
                  maxLimits={maxLimitsCS}
                  value={selectedRowCS.length}
                  resourseName="obras"
                >
                  <Table
                    showHeader={false}
                    isSpaced
                    columns={getColumnsConstructionSitesInactivate({})}
                    dataSource={getDowngradeInfo?.ConstructionSites?.map(
                      (constructionSite: any, index: number) => (
                        { ...constructionSite, key: index }
                      )
                    )}
                    rowSelection={rowSelectionCS}
                    size="middle"
                  />
                </BoxDownResources>
              )}
            </>
          }

          {(downgradeRequest.showWarning === 1 ||
            downgradeRequest.showWarning === 3) &&
            <>
              {isLoading ? (
                <Skeleton active />
              ) : (
                <BoxDownResources
                  title={t('faturamento:downgrade.inactivateUsersTitle')}
                  maxLimits={maxLimitsTenants}
                  value={selectedRowTenant.length}
                  resourseName="usuários"
                >
                  <Table
                    showHeader={false}
                    isSpaced
                    columns={getColumnsTenantsInactivate({})}
                    dataSource={getDowngradeInfo?.TenantUsers?.map(
                      (tenants: any, index: number) => (
                        { ...tenants, key: index }
                      )
                    )}
                    rowSelection={rowSelectionTenant}
                    size="middle"
                  />
                </BoxDownResources>
              )}
            </>
          }
        </div>

        <div className={styles['grid-right']}>
          {changePlan && <BoxPlan
            title={t('faturamento:downgrade.chosenPlan')}
            plan={changePlan}
            buttonPlanDowngrade={buttonPlanDowngrade}
          />}
        </div>

        {showModalExitDowngrade && (
          <ExitRedirect
            visible={showModalExitDowngrade}
            onClose={onClose}
          />
        )}
      </div>
    </>
  )
}

const mapState = (state: IGlobalReducerState) => ({
  showModalExitDowngrade: state.revenues.showModalExitDowngrade,
  downgradeRequest: state.revenues.showWarningDowngradePlan,
  changePlan: state.revenues.changePlan,
  userInfo: state.auth.userInfo,
  getDowngradeInfo: state.revenues.getDowngradeInfo,
  isLoading: state.revenues.isLoading,
  downgradeRequestLoading: state.revenues.downgradeRequestLoading
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>
type Props = PropsFromRedux;

export default connector(Downgrade);
